<template>
    <landlords-find
        :params="{
            landlord: $route.params.landlord
        }"
        :include="[
            'account(include:persons(filters:is_account_opener=true|include:address,identity_card,proof_of_address))'
        ]"
        @success="onLandlordsFind"
    >
        <div
            slot-scope="{
                data: landlord,
                error: find_error,
                has_loaded,
                find
            }"
        >
            <template v-if="!has_loaded || find_error">
                <base-loading
                    :error="find_error"
                    :retry="find"
                    class="py-6"
                />

                <error-modal
                    v-if="find_error"
                    :error="find_error"
                />
            </template>

            <accounts-update
                v-else
                :params="{
                    account: landlord.account.uuid
                }"
            >
                <persons-update
                    slot-scope="{
                        sid: account_sid,
                        promise: accountsUpdate
                    }"
                    :params="{
                        person: person.uuid
                    }"
                >
                    <promise-all
                        slot-scope="{
                            sid: person_sid,
                            promise: personsUpdate
                        }"
                        :execute-on-created="false"
                        :callstack="{
                            account: () => accountsUpdate(forms.account),
                            person: () => personsUpdate(forms.person)
                        }"
                        @success="onSuccess"
                        @error="onError"
                    >
                        <base-form
                            slot-scope="{
                                is_loading: is_saving,
                                errors: update_errors,
                                execute: update
                            }"
                            @submit="update"
                        >
                            <div class="px-4 py-6 border-b sm:px-6">
                                <validator-alert :sid="[account_sid, person_sid]" />

                                <form-section>
                                    <template slot="title">Account</template>
                                    <template slot="description">
                                        What type of account do you require?
                                    </template>

                                    <form-item
                                        :sid="account_sid"
                                        :is-required="true"
                                        name="type"
                                    >
                                        <template slot="label">Type</template>

                                        <div class="flex flex-col sm:flex-row">
                                            <input-radio-card
                                                v-model="forms.account.type"
                                                :sid="account_sid"
                                                value="individual"
                                                name="type"
                                                :disabled="is_saving || landlord.account.number_of_verification_attempts > 0"
                                                class="mb-3 sm:mb-0 sm:mr-3"
                                            >
                                                <template slot="label">Individual</template>
                                                <template slot="hint">
                                                    You’re creating this account for you, to
                                                    operate as an individual/sole trader.
                                                </template>
                                            </input-radio-card>

                                            <input-radio-card
                                                v-model="forms.account.type"
                                                :sid="account_sid"
                                                value="company"
                                                name="type"
                                                :disabled="is_saving || landlord.account.number_of_verification_attempts > 0"
                                                class="sm:mb-0 sm:mr-3"
                                            >
                                                <template slot="label">Business</template>
                                                <template slot="hint">
                                                    You’re creating this account for a Private Limited Company, LLP or similar.
                                                </template>
                                            </input-radio-card>
                                        </div>
                                    </form-item>

                                    <div class="flex">
                                        <base-svg
                                            name="icon-important-circle"
                                            class="w-5 h-5 mr-3px"
                                        />

                                        <span class="text-xs leading-tight text-grey-darker mt-2px">
                                            Business accounts must be verfied by someone authorized to sign on behalf of the
                                            organisation - who will now be referred to with “you” and “your”.
                                        </span>
                                    </div>
                                </form-section>
                            </div>

                            <div class="px-4 pt-6 border-b sm:px-6">
                                <form-section>
                                    <template slot="title">Basics</template>
                                    <template slot="description">
                                        <strong>Your details.</strong> No abbreviations,
                                        no nicknames - <strong>details as they appear on your ID.</strong>
                                    </template>

                                    <grid-layout>
                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="person_sid"
                                                :is-required="true"
                                                name="first_name"
                                            >
                                                <template slot="label">First Name</template>

                                                <input-text
                                                    v-model="forms.person.first_name"
                                                    :sid="person_sid"
                                                    :disabled="is_saving || person.status === person.consts.status.verified"
                                                    name="first_name"
                                                />
                                            </form-item>
                                        </grid-layout-item>

                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="person_sid"
                                                :is-required="true"
                                                name="last_name"
                                            >
                                                <template slot="label">Last Name</template>

                                                <input-text
                                                    v-model="forms.person.last_name"
                                                    :sid="person_sid"
                                                    :disabled="is_saving || person.status === person.consts.status.verified"
                                                    name="last_name"
                                                />
                                            </form-item>
                                        </grid-layout-item>

                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="person_sid"
                                                :is-required="true"
                                                name="date_of_birth"
                                            >
                                                <template slot="label">Date of Birth</template>

                                                <input-date
                                                    v-model="forms.person.date_of_birth"
                                                    :sid="person_sid"
                                                    :disabled="is_saving || person.status === person.consts.status.verified"
                                                    name="date_of_birth"
                                                />
                                            </form-item>
                                        </grid-layout-item>

                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="person_sid"
                                                :is-required="true"
                                                name="telephone"
                                            >
                                                <template slot="label">Telephone</template>

                                                <input-telephone
                                                    v-model="forms.person.telephone"
                                                    :sid="person_sid"
                                                    :disabled="is_saving"
                                                    name="telephone"
                                                />
                                            </form-item>
                                        </grid-layout-item>
                                    </grid-layout>
                                </form-section>
                            </div>

                            <div class="px-4 pt-6 border-b sm:px-6">
                                <form-section>
                                    <template slot="title">Address</template>
                                    <template slot="description">
                                        Again, we require <strong>your address.</strong>
                                    </template>

                                    <form-items-address
                                        v-model="forms.person.address"
                                        :sid="person_sid"
                                        :disabled="is_saving"
                                    />
                                </form-section>
                            </div>

                            <div class="px-4 pt-6 border-b sm:px-6">
                                <form-section>
                                    <template slot="title">Proof of Identity</template>
                                    <template slot="description">
                                        Upload a clear, high-resolution scan (or photo) of your
                                        <a
                                            href="https://help.rentora.co.uk/article/47-what-proof-of-identity-documents-are-acceptable"
                                            class="text-blue"
                                            target="_blank"
                                            rel="noopener"
                                        >
                                            proof of identity.
                                        </a>
                                    </template>

                                    <grid-layout>
                                        <grid-layout-item>
                                            <form-item-identity-document
                                                v-model="forms.person.identity_card"
                                                :identity-document="person.identity_card"
                                                :sid="person_sid"
                                                :disabled="is_saving || person.status === person.consts.status.verified"
                                                :is-required="true"
                                                name="identity_card"
                                            />
                                        </grid-layout-item>
                                    </grid-layout>
                                </form-section>
                            </div>

                            <div class="px-4 pt-6 border-b sm:px-6">
                                <form-section>
                                    <template slot="title">Proof of Address</template>
                                    <template slot="description">
                                        Upload a clear, high-resolution scan (or photo) of your
                                        <a
                                            href="https://help.rentora.co.uk/article/48-what-proof-of-address-documents-are-acceptable"
                                            class="text-blue"
                                            target="_blank"
                                            rel="noopener"
                                        >
                                            proof of address.
                                        </a>
                                    </template>

                                    <grid-layout>
                                        <grid-layout-item>
                                            <form-item-identity-document
                                                v-model="forms.person.proof_of_address"
                                                :identity-document="person.proof_of_address"
                                                :sid="person_sid"
                                                :disabled="is_saving || person.status === person.consts.status.verified"
                                                :is-required="true"
                                                name="proof_of_address"
                                            />
                                        </grid-layout-item>
                                    </grid-layout>
                                </form-section>
                            </div>

                            <div class="px-4 py-3 text-right bg-grey-bg sm:px-6">
                                <base-button
                                    :is-loading="is_saving"
                                    class="w-full btn-blue md:w-auto"
                                >
                                    Update Account &amp; Continue
                                </base-button>
                            </div>

                            <div
                                v-for="(update_error, index) in update_errors"
                                :key="index"
                            >
                                <error-modal
                                    v-if="update_error && $error.is(update_error, { exclude: [400, 422] })"
                                    :error="update_error"
                                />
                            </div>
                        </base-form>
                    </promise-all>
                </persons-update>
            </accounts-update>
        </div>
    </landlords-find>
</template>

<script>
    import _ from 'lodash'
    import moment from 'moment'
    import scroll from '../../../../../support/scroll'
    import AccountsUpdate from '../../../../../components/accounts-update'
    import FormItemIdentityDocument from '../../../../../components/form-item-identity-document'
    import FormItemsAddress from '../../../../../components/form-items-address'
    import InputRadioCard from '../../../../../components/input-radio-card'
    import LandlordsFind from '../../../../../components/landlords-find'
    import PersonsUpdate from '../../../../../components/persons-update'
    import PromiseAll from '../../../../../components/promise-all'

    export default {
        components: {
            AccountsUpdate,
            FormItemIdentityDocument,
            FormItemsAddress,
            InputRadioCard,
            LandlordsFind,
            PersonsUpdate,
            PromiseAll
        },
        data () {
            return {
                person: null,
                forms: {
                    account: {
                        type: null,
                        description: null
                    },
                    person: {
                        first_name: null,
                        last_name: null,
                        date_of_birth: null,
                        address: {
                            street: null,
                            locality: null,
                            village: null,
                            town: null,
                            postcode: null,
                            county: null
                        },
                        telephone: null,
                        identity_card: null,
                        proof_of_address: null
                    }
                }
            }
        },
        methods: {
            onSuccess ({ account: { data: account } }) {
                this.$track(
                    'dashboard.landlords.verification.account.edit',
                    'form-submission',
                    'A landlord has completed step 1 (account) of verifying their account'
                )

                this.$emit('success')

                setTimeout(() => {
                    this.$router.push({
                        name: this.$router.fix(
                            account.type === account.consts.type.business
                                ? 'dashboard.*.verification.business.edit'
                                : 'dashboard.*.verification.bank.edit'
                        )
                    })
                }, 0)
            },
            onError () {
                scroll.toTop()
            },
            onLandlordsFind ({ data: landlord }) {
                this.forms.account.type = landlord.account.type
                this.forms.account.description = landlord.account.description

                this.person = landlord.account.persons.data[0]

                this.forms.person.first_name = this.person.first_name
                this.forms.person.last_name = this.person.last_name
                this.forms.person.date_of_birth = this.person.date_of_birth
                    ? moment(this.person.date_of_birth).format('DD/MM/YYYY')
                    : null
                this.forms.person.telephone = this.person.telephone
                    ? _.pick(this.person.telephone, ['suffix', 'prefix'])
                    : null

                if (this.person.address) {
                    this.forms.person.address.street = this.person.address.street
                    this.forms.person.address.locality = this.person.address.locality
                    this.forms.person.address.village = this.person.address.village
                    this.forms.person.address.town = this.person.address.town
                    this.forms.person.address.postcode = this.person.address.postcode
                    this.forms.person.address.county = this.person.address.county
                }
            }
        }
    }
</script>
